import React from 'react';
import ReactDOM from 'react-dom/client';
// Self-hosted variable fonts. Onest for UI / body (modern, Cyrillic-first
// coverage), JetBrains Mono for code, IDs, hex values, kbd shortcuts.
// Variable means one file per family, every weight free.
import '@fontsource-variable/onest';
import '@fontsource-variable/jetbrains-mono';
import { App } from './App';
import i18n from '@/i18n';
import { POPUP_DONE_PATH, finishPopupSignIn, framePresenceChannel, installPlatformEmbed } from '@/lib/platformEmbed';
import { followDesktopLanguage } from './lib/desktopLanguage';
import { desktopPresenceChannel } from './lib/desktopPresence';
import { installShellPresence } from './lib/shellPresence';
import { installShellNotifications } from './lib/shellNotifications';
import { setStatusFor } from './lib/statusTimer';
import { useAuthStore } from './stores/authStore';
import { initSentry } from '@/lib/sentry';
import { installDevSeed } from '@/lib/devSeed';
import { installMockMode } from '@/lib/mockMode';
import { hydrateTheme } from '@/lib/theme';
import { hydrateSkin } from '@/lib/skin';
import { hydrateFont } from '@/lib/fontPreference';
import { installUnreadTitle } from '@/lib/unreadTitle';
import { installFaviconBadge } from '@/lib/faviconBadge';
import { installDesktopBadge } from '@/lib/desktopBadgeSync';
import './index.css';

// Apply the stored theme + interface font BEFORE the first paint, so users
// that picked light theme / a non-default font last session never see a
// flash of the wrong design. data-skin first (the CSS layer keys off it).
hydrateSkin();
hydrateTheme();
hydrateFont();

// The Cispracs ID sign-in popup opened from the platform's Chat frame comes
// back here: tell the frame the session is ready and close. The app itself
// does not boot in the popup.
const popupDone = window.location.pathname === POPUP_DONE_PATH;
if (popupDone) finishPopupSignIn();

// Inside the platform's Chat frame (voice#347): language from the host,
// in-frame history, and the postMessage contract. A no-op everywhere else.
installPlatformEmbed({
  changeLanguage: (language) => {
    document.documentElement.lang = language;
    void i18n.changeLanguage(language);
  },
});

// Inside the Desktop platform shell: the shell's language, live (lib/desktopLanguage.ts).
followDesktopLanguage(window.bunkerDesktop, (language) => {
  document.documentElement.lang = language;
  void i18n.changeLanguage(language);
});

// The presence status in the shell's account menu, Bunker owning it (lib/shellPresence.ts).
const presenceChannel =
  framePresenceChannel(() => useAuthStore.getState().user?.platformId ?? null) ?? desktopPresenceChannel(window.bunkerDesktop);
if (presenceChannel) {
  installShellPresence(presenceChannel, {
    current: () => useAuthStore.getState().user?.status ?? null,
    subscribe: (cb) => useAuthStore.subscribe((state, previous) => {
      if (state.user?.status !== previous.user?.status) cb();
    }),
    set: (status) => setStatusFor(status, null),
  });
}

// Inside the shell's Chat frame: unread counts and pop-ups go to the shell (lib/shellNotifications.ts).
installShellNotifications();

// Electron desktop shell marker. On Windows the WebContentsView GPU compositor
// leaves transparent tiles BLACK while scrolling content that uses mask-image
// (HeroUI ScrollShadow edge-fades) — fine in a real browser, broken in the
// shell. Tag <html> so index.css can drop those (cosmetic) masks on desktop.
if (window.bunkerDesktop?.isDesktop) {
  document.documentElement.classList.add('is-desktop');
}

// Error monitoring (prod builds only; no-op in dev / mock). Init early so a
// crash during the first render is still captured.
initSentry();

// Dev-only mock mode (`?mock=full`) — stubs every /api/* so the chat
// shell renders without a backend. Runs BEFORE React mounts so the very
// first /api/me hit lands on the stub.
installMockMode();

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('#root not found in index.html');
}

ReactDOM.createRoot(rootEl).render(
  popupDone ? (
    // Shown only when the browser kept the popup open (window.close refused).
    <p className="grid min-h-full place-items-center bg-bg p-8 text-center text-sm text-ink-dim">{i18n.t('auth.popupDone')}</p>
  ) : (
    <React.StrictMode>
      <App />
    </React.StrictMode>
  ),
);

// Dev-only badge seeder. No-op in production builds. Auto-applies when
// the URL has `?seed` (light) or `?seed=heavy`; manually callable via
// `window.bunkerSeed.heavy()` from the console.
installDevSeed();

// Reflect unread / mention counts into document.title so the browser
// tab surfaces "you have new stuff" while Bunker is in the background.
installUnreadTitle();
// Same totals, painted as a badge on the favicon (red pill for mentions,
// accent dot for plain unread).
installFaviconBadge();
// And onto the Windows taskbar icon when running inside the Electron shell
// (no-op in a plain browser).
installDesktopBadge();
